import React from 'react';
import { Box, CircularProgress, Typography, styled, keyframes } from '@mui/material';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const StyledContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, 
    ${theme.palette.primary.dark} 0%, 
    ${theme.palette.primary.main} 25%,
    ${theme.palette.secondary.main} 75%,
    ${theme.palette.secondary.dark} 100%)`,
  backgroundSize: '400% 400%',
  animation: `${shimmer} 8s ease-in-out infinite`,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
    animation: `${fadeIn} 2s ease-in-out`
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '300px',
    height: '300px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
    borderRadius: '50%',
    animation: `${pulse} 3s ease-in-out infinite`
  }
}));

const LogoContainer = styled(Box)({
  animation: `${float} 4s ease-in-out infinite`,
  zIndex: 1,
  position: 'relative'
});

const SpinnerContainer = styled(Box)({
  marginTop: 32,
  zIndex: 1,
  position: 'relative'
});

const LoadingText = styled(Typography)(({ theme }) => ({
  marginTop: 24,
  color: 'rgba(255, 255, 255, 0.9)',
  fontWeight: 300,
  letterSpacing: 2,
  textAlign: 'center',
  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
  zIndex: 1,
  position: 'relative',
  animation: `${fadeIn} 1s ease-in-out 0.5s both`
}));

const SubtitleText = styled(Typography)(({ theme }) => ({
  marginTop: 8,
  color: 'rgba(255, 255, 255, 0.7)',
  fontWeight: 200,
  letterSpacing: 1,
  fontSize: '0.9rem',
  textAlign: 'center',
  zIndex: 1,
  position: 'relative',
  animation: `${fadeIn} 1s ease-in-out 0.8s both`
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
  marginTop: 4,
  color: 'rgba(255, 255, 255, 0.6)',
  fontWeight: 200,
  letterSpacing: 0.5,
  fontSize: '0.75rem',
  textAlign: 'center',
  zIndex: 1,
  position: 'relative',
  animation: `${fadeIn} 1s ease-in-out 1.1s both`,
  maxWidth: '300px',
  lineHeight: 1.4
}));

/**
 * Protected route loading messages configuration
 * Only protected routes (wrapped in AuthGuard) will show this loading screen
 * Add new routes here as needed - use path patterns as keys
 */
const ROUTE_MESSAGES = {
  // Dashboard & Main
  '/dashboard': {
    title: 'Welcome Back',
    subtitle: 'Loading your dashboard...',
    description: 'Preparing your personalized overview and statistics'
  },

  // Generation Tools
  '/prompt_generator': {
    title: 'Prompt Generator',
    subtitle: 'Loading generator...',
    description: 'Preparing your AI prompt generation tools'
  },
  '/magic_generator': {
    title: 'Smart Generator',
    subtitle: 'Loading smart tools...',
    description: 'Initializing intelligent content generation'
  },
  '/add_image_in_post': {
    title: 'Image Generator',
    subtitle: 'Loading image tools...',
    description: 'Preparing your image generation workspace'
  },

  // Content Management
  '/saved': {
    title: 'Saved Prompts',
    subtitle: 'Loading your prompts...',
    description: 'Fetching your saved prompt collection'
  },
  '/presets': {
    title: 'Saved Presets',
    subtitle: 'Loading presets...',
    description: 'Retrieving your custom presets'
  },
  '/history': {
    title: 'Generation History',
    subtitle: 'Loading history...',
    description: 'Fetching your generation records'
  },

  // WordPress
  '/wordpress_history': {
    title: 'WordPress History',
    subtitle: 'Loading WordPress data...',
    description: 'Fetching your WordPress activity'
  },
  '/add_image_to_post': {
    title: 'Add Image to Post',
    subtitle: 'Loading editor...',
    description: 'Preparing the image editor for your post'
  },

  // Social Media
  '/pinterest': {
    title: 'Pinterest',
    subtitle: 'Loading Connected Pinterest Accounts...',
    description: ''
  },
  '/instagram': {
    title: 'Instagram',
    subtitle: 'Loading Connected Instagram Accounts...',
    description: ''
  },
  '/social_images': {
    title: 'Social Images',
    subtitle: 'Loading your images...',
    description: 'Fetching your social media image history'
  },

  // Settings & Account
  '/settings': {
    title: 'Settings',
    subtitle: 'Loading settings...',
    description: 'Preparing your configuration options'
  },
  '/account': {
    title: 'Account Profile',
    subtitle: 'Loading profile...',
    description: 'Fetching your account information'
  },
  '/credentials': {
    title: 'Credentials',
    subtitle: 'Loading credentials...',
    description: 'Preparing your API credentials securely'
  },
  '/prompt_settings': {
    title: 'Prompt Settings',
    subtitle: 'Loading prompt config...',
    description: 'Fetching your prompt configuration'
  },

  // Pricing & Payments
  '/pricing': {
    title: 'Pricing Plans',
    subtitle: 'Loading plans...',
    description: 'Fetching available subscription options'
  },
  '/payment/invoices': {
    title: 'Invoices',
    subtitle: 'Loading invoices...',
    description: 'Fetching your billing history'
  },

  // API & Tools
  '/alphonse_api': {
    title: 'Alphonse API',
    subtitle: 'Loading API tools...',
    description: 'Preparing your API documentation and tools'
  },
  '/exifs': {
    title: 'Manage EXIF',
    subtitle: 'Loading EXIF manager...',
    description: 'Preparing your image metadata tools'
  },
  '/editPanel': {
    title: 'Edit Panel',
    subtitle: 'Loading editor...',
    description: 'Preparing your editing workspace'
  },

  // Tracking
  '/track': {
    title: 'Tracking',
    subtitle: 'Loading Image Generation...',
    description: 'Fetching your tracking information'
  }
};

// Default message for any protected route not explicitly defined
const DEFAULT_MESSAGE = {
  title: 'Welcome Back',
  subtitle: 'Verifying your session...',
  description: 'Loading your personalized dashboard and settings'
};

/**
 * Get loading message for a given path
 * Supports exact matches and pattern matching for dynamic routes
 */
const getRouteMessage = (path) => {
  // Try exact match first
  if (ROUTE_MESSAGES[path]) {
    return ROUTE_MESSAGES[path];
  }

  // Try pattern matching for dynamic routes (e.g., /track/:trackID, /add_image_in_post/:siteID/:postID)
  for (const [pattern, message] of Object.entries(ROUTE_MESSAGES)) {
    if (path.startsWith(pattern)) {
      return message;
    }
  }

  return DEFAULT_MESSAGE;
};

const AuthLoadingScreen = ({ currentPath }) => {
  const content = getRouteMessage(currentPath);

  return (
    <StyledContainer>
      <LogoContainer>
        <img
          src="/assets/images/logo_blue.png"
          alt="Logo"
          style={{
            width: 'auto',
            height: '80px',
            filter: 'brightness(0) invert(1) drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
            transition: 'all 0.3s ease'
          }}
        />
      </LogoContainer>
      <SpinnerContainer>
        <CircularProgress
          size={60}
          thickness={3}
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
          }}
        />
      </SpinnerContainer>
      <LoadingText variant="h4">{content.title}</LoadingText>
      <SubtitleText variant="body1">{content.subtitle}</SubtitleText>
      <DescriptionText variant="body2">{content.description}</DescriptionText>
    </StyledContainer>
  );
};

export default AuthLoadingScreen;
